import { PlatformSource } from '@/view-models/platform'
import { usePlatformStore } from '@/store-pinia/platform'
import { RemarkIdEnum } from '@/view-models/remark'
import { phoneHelpers } from '@/utils/phone'
import { COMMON_DEFAULT_COUNTRY_CODE, ELITE_DEFAULT_COUNTRY_CODE } from '@/constants/payment'

interface CardholderDefaultsOptions {
  /**
   * 備註表單資料 (for 一般預訂流程)
   * 從 booking store 的 remark fields 取得
   */
  remarkFormData?: Record<number, string>
  /**
   * 是否為付款連結 (FunBook Prepayment)
   * 用於判斷是否為 SaaS 付款連結場景
   */
  isPaymentLink?: boolean
}

/**
 * 持卡人表單欄位預設值邏輯
 * 根據不同平台和場景提供不同的預設值
 */
export function useCardholderDefaults(options: Ref<CardholderDefaultsOptions> | ComputedRef<CardholderDefaultsOptions>) {
  const nuxtApp = useNuxtApp()
  const { $auth } = nuxtApp
  const route = useRoute()
  const platformStore = usePlatformStore()

  // 取得用戶資訊
  const userInfo = computed(() => ($auth as any).user)

  // 平台判斷
  const platform = computed(() => platformStore.name)
  const isFunBookV2 = computed(() => platform.value === PlatformSource.FunBook)

  // SaaS 付款連結判斷 - 從 props 取得而非 route query
  const isSaaSPaymentLink = computed(() => {
    const opts = unref(options)
    return isFunBookV2.value && !!opts.isPaymentLink
  })

  // SaaS 一般付費方案判斷
  const isSaaSNormalPlan = computed(() => {
    return isFunBookV2.value && !isSaaSPaymentLink.value
  })

  // 是否為登入狀態
  const isLoggedIn = computed(() => !!userInfo.value)

  // ELITE Membership page 判斷 (從 route 判斷)
  const isEliteMembership = computed(() => {
    return route.path?.includes('/elite') || route.name?.toString().includes('elite')
  })

  /**
   * 取得持卡人姓名預設值
   */
  const defaultName = computed(() => {
    // SaaS 付款連結：無需記憶，每次皆為空值
    if (isSaaSPaymentLink.value) {
      return ''
    }

    // FunNow, SaaS 一般付費方案
    return userInfo.value?.card_holder_name || ''
  })

  /**
   * 取得持卡人電話預設值
   */
  const defaultPhone = computed(() => {
    // SaaS 付款連結：無需記憶，國碼自動帶入分店所在地，號碼欄位留空
    if (isSaaSPaymentLink.value) {
      return {
        phone: '',
        countryCode: COMMON_DEFAULT_COUNTRY_CODE,
      }
    }

    // SaaS 一般付費方案
    if (isSaaSNormalPlan.value) {
      // 優先順序：member 電話 > 備註欄位電話
      const memberPhone = isLoggedIn.value ? userInfo.value?.phone : ''
      const remarkPhone = getRemarkPhone()

      if (memberPhone) {
        return phoneHelpers.parsePhoneNumber(memberPhone)
      }
      if (remarkPhone) {
        return phoneHelpers.parsePhoneNumber(remarkPhone)
      }

      // 兩者皆無：國碼自動帶入分店所在地，號碼欄位留空
      return {
        phone: '',
        countryCode: COMMON_DEFAULT_COUNTRY_CODE,
      }
    }

    // FunNow
    const memberPhone = userInfo.value?.phone || ''
    const remarkPhone = getRemarkPhone()

    // 優先順序：member 電話 > 備註欄位電話
    if (memberPhone) {
      return phoneHelpers.parsePhoneNumber(memberPhone)
    }
    if (remarkPhone) {
      return phoneHelpers.parsePhoneNumber(remarkPhone)
    }

    // 兩者皆無
    if (isEliteMembership.value) {
      // ELITE Membership page: 國碼自動帶入馬來西亞
      return {
        phone: '',
        countryCode: ELITE_DEFAULT_COUNTRY_CODE,
      }
    }

    // FunNow: 國碼自動帶入台灣
    return {
      phone: '',
      countryCode: COMMON_DEFAULT_COUNTRY_CODE,
    }
  })

  /**
   * 取得持卡人 Email 預設值
   */
  const defaultEmail = computed(() => {
    // SaaS 付款連結：無需記憶，每次皆為空值
    if (isSaaSPaymentLink.value) {
      return ''
    }

    const memberEmail = userInfo.value?.email || ''
    const remarkEmail = getRemarkEmail()

    // 優先順序：member Email > 備註欄位 Email
    return memberEmail || remarkEmail || ''
  })

  /**
   * 從備註表單資料中取得電話
   */
  function getRemarkPhone(): string {
    const opts = unref(options)
    if (!opts.remarkFormData) return ''
    // RemarkIdEnum.PHONE_NUMBER = 2
    return opts.remarkFormData[RemarkIdEnum.PHONE_NUMBER] || ''
  }

  /**
   * 從備註表單資料中取得 Email
   */
  function getRemarkEmail(): string {
    const opts = unref(options)
    if (!opts.remarkFormData) return ''
    // RemarkIdEnum.EMAIL = 5
    return opts.remarkFormData[RemarkIdEnum.EMAIL] || ''
  }

  return {
    defaultName,
    defaultPhone,
    defaultEmail,
    platform,
    isSaaSPaymentLink,
    isSaaSNormalPlan,
    isEliteMembership,
    isLoggedIn,
  }
}
